import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ClipboardList, Plus } from "lucide-react";

export default function Audit() {
  const [audits, setAudits] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ warehouse_id: "", notes: "", lines: [] });

  const load = async () => {
    const [a, inv, w] = await Promise.all([
      api.get("/warehouse/audits"),
      api.get("/warehouse/inventory"),
      api.get("/inventory/warehouses"),
    ]);
    setAudits(a.data); setInventory(inv.data); setWarehouses(w.data);
  };
  useEffect(() => { load(); }, []);

  const startCount = (wh_id) => {
    setForm({
      warehouse_id: wh_id,
      notes: "",
      lines: inventory.slice(0, 20).map((i) => ({
        item_id: i.item_id, item_name: i.name, item_sku: i.sku,
        system_qty: i.total_qty, counted_qty: i.total_qty,
      })),
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      await api.post("/warehouse/audits", form);
      toast.success("Audit posted — variances adjusted");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="warehouse-audit">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-primary" /> Cycle Count & Audit
          </h1>
          <p className="text-muted-foreground">Physical stock count with auto-adjusting variance posting.</p>
        </div>
        <div className="flex gap-2 items-end">
          <Select onValueChange={(v) => startCount(v)}>
            <SelectTrigger className="w-64" data-testid="aud-start"><SelectValue placeholder="Start audit in warehouse…" /></SelectTrigger>
            <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Number</TableHead><TableHead>Warehouse</TableHead>
            <TableHead className="text-right">Lines</TableHead><TableHead className="text-right">Total variance</TableHead>
            <TableHead>Status</TableHead><TableHead>Created</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {audits.map((a) => (
              <TableRow key={a.id} data-testid={`aud-row-${a.number}`}>
                <TableCell className="font-mono text-xs">{a.number}</TableCell>
                <TableCell>{a.warehouse_name}</TableCell>
                <TableCell className="text-right">{a.lines?.length}</TableCell>
                <TableCell className="text-right font-mono">{a.total_variance}</TableCell>
                <TableCell><Badge>{a.status}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{(a.created_at || "").slice(0, 10)}</TableCell>
              </TableRow>
            ))}
            {audits.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No audits yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Cycle count — enter physical counts</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {form.lines.map((l, i) => {
              const variance = (l.counted_qty || 0) - (l.system_qty || 0);
              return (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-6 text-sm">
                    <div className="font-mono text-xs text-muted-foreground">{l.item_sku}</div>
                    <div>{l.item_name}</div>
                  </div>
                  <div className="col-span-2 text-sm text-muted-foreground">Sys: {l.system_qty}</div>
                  <div className="col-span-2">
                    <Input type="number" value={l.counted_qty} onChange={(e) => setForm({ ...form, lines: form.lines.map((x, idx) => idx === i ? { ...x, counted_qty: parseFloat(e.target.value || "0") } : x) })} data-testid={`aud-count-${i}`} />
                  </div>
                  <div className={`col-span-2 text-sm font-mono ${variance === 0 ? "text-muted-foreground" : variance > 0 ? "text-emerald-600" : "text-red-600"}`}>
                    Δ {variance > 0 ? "+" : ""}{variance}
                  </div>
                </div>
              );
            })}
          </div>
          <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="aud-save">Post audit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
